import { useQuery } from "@tanstack/react-query";
import React from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

const getCampaignsV2 = (
  startDate?: string | null,
  endDate?: string | null,
  brand?: string,
  category?: string,
  product?: string,
  geography?: string
): Promise<string[]> => {
  return axiosApi.get("/api/v2/campaigns/all", {
    baseURL: INSIGHTS_BACKEND_URL,
    params: { startDate, endDate, brand, category, product, geography },
  });
};

const _getCampaignsNest = (
  startDate?: string | null,
  endDate?: string | null,
  brand?: string,
  category?: string,
  product?: string,
  geography?: string
): Promise<string[]> => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (geography) params.append("geography", geography);

  return axiosApi
    .get(`/api/campaigns/all?${params.toString()}`, {
      baseURL: NEST_BACKEND_URL,
    })
    .then((response: any) => {
      return response.campaigns;
    });
};

export const useCampaigns = ({
  startDate,
  endDate,
  config,
  brand,
  category,
  product,
  geography,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand?: string;
  category?: string;
  product?: string;
  geography?: string;
  config?: QueryConfig<typeof getCampaignsV2 | typeof _getCampaignsNest>;
} = {}) => {
  const endpointType = useModuleEndpoint("campaigns");

  const queryKey = React.useMemo(
    () => ["campaigns", startDate, endDate, brand, category, product, geography, endpointType],
    [startDate, endDate, brand, category, product, geography, endpointType]
  );

  const queryFn = React.useCallback(() => {
    if (endpointType === "new") {
      return _getCampaignsNest(startDate, endDate, brand, category, product, geography);
    } else {
      return getCampaignsV2(startDate, endDate, brand, category, product, geography);
    }
  }, [startDate, endDate, brand, category, product, geography, endpointType]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
